import {
  FreightRail,
  FreightTruck,
  IDestinationExtension,
  IQuoteExtension,
  OriginPrice,
} from "../../../types/ICompAnalysisQMD";
import { PricePreference } from "./KochDlvdOfferToggle";

type DisplayResult = {
  display: string;
  numeric: number | null;
};

const toDisplay = (value: OriginPrice, dollarSign = true): DisplayResult => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    if (value === "DISCUSS") return { display: "DISC", numeric: null };
    if (value === "NO_OFFER") return { display: "N/O", numeric: null };
    return { display: "--", numeric: null };
  }

  return {
    display: dollarSign ? `$${value.toFixed(0)}` : value.toFixed(0),
    numeric: value,
  };
};

const getNetback = (
  quote: IQuoteExtension,
  kochPricePreference: PricePreference,
): DisplayResult => {
  const mode = quote.mode.toUpperCase();

  if (!quote.competitor && mode === "TRUCK") {
    if (kochPricePreference.truck === "px") {
      return toDisplay(quote.postedPrice);
    }
    return toDisplay(quote.truckPrice);
  }

  if (!quote.competitor && mode === "RAIL") {
    if (kochPricePreference.rail === "px") {
      return toDisplay(quote.postedPrice);
    }
    return toDisplay(quote.railPrice);
  }

  if (quote.competitor && mode === "TRUCK") {
    return toDisplay(quote.truckPrice);
  }

  if (quote.competitor && mode === "RAIL") {
    return toDisplay(quote.railPrice);
  }

  return { display: "--", numeric: null };
};

const getNetbackSubText = (quote: IQuoteExtension): string => {
  const mode = quote.mode.toUpperCase();
  const postedPrice = toDisplay(quote.postedPrice, false);
  const truckPrice = toDisplay(quote.truckPrice, false);
  const railPrice = toDisplay(quote.railPrice, false);

  if (!quote.competitor && mode === "TRUCK") {
    return `(${postedPrice.display || "--"} - ${truckPrice.display || "--"})`;
  }

  if (!quote.competitor && mode === "RAIL") {
    return `(${postedPrice.display || "--"} - ${railPrice.display || "--"})`;
  }

  if (quote.competitor && mode === "TRUCK") {
    return `(${truckPrice.display || "--"})`;
  }

  if (quote.competitor && mode === "RAIL") {
    return `(${railPrice.display || "--"})`;
  }

  return "--";
};

const getFreight = (freight?: FreightTruck | FreightRail): DisplayResult => {
  if (!freight) return { display: "--", numeric: null };
  return {
    display: freight.totalFreight.toFixed(2),
    numeric: Number(freight.totalFreight.toFixed(2)),
  };
};

// TODO: do we want to always use initialOffer (posted price + freight)?
const determinePriceSetter = (
  kochDlvdOfferMode: PricePreference,
  cheapestKochQuote?: IQuoteExtension,
  cheapestCompetitorQuote?: IQuoteExtension,
): "KOCH" | "COMPETITOR" | "NEITHER" => {
  if (!cheapestKochQuote && !cheapestCompetitorQuote) return "NEITHER";
  if (!cheapestKochQuote) return "COMPETITOR";
  if (!cheapestCompetitorQuote) return "KOCH";

  const kochNetback = getNetback(cheapestKochQuote, kochDlvdOfferMode);
  const kochFreight = getFreight(cheapestKochQuote.freight);
  const kochDeliveredValue =
    kochNetback.numeric !== null && kochFreight.numeric !== null
      ? kochNetback.numeric + kochFreight.numeric
      : 0;
  const competitorNetback = getNetback(
    cheapestCompetitorQuote,
    kochDlvdOfferMode,
  );
  const competitorFreight = getFreight(cheapestCompetitorQuote.freight);
  const competitorDeliveredValue =
    competitorNetback.numeric !== null && competitorFreight.numeric !== null
      ? competitorNetback.numeric + competitorFreight.numeric
      : 0;

  return kochDeliveredValue <= competitorDeliveredValue ? "KOCH" : "COMPETITOR";
};

const getDeliveredOffers = (
  netback: DisplayResult,
  freight: DisplayResult,
  kochPricePreference: PricePreference,
  cheapestKochQuote?: IQuoteExtension,
  cheapestCompetitorQuote?: IQuoteExtension,
) => {
  const deliveredPrice =
    netback.numeric !== null && freight.numeric !== null
      ? String(Math.round(netback.numeric + freight.numeric))
      : "--";

  if (!cheapestKochQuote) {
    return { deliveredPrice, competitorDeliveredPrice: deliveredPrice };
  }
  if (!cheapestCompetitorQuote) {
    return { deliveredPrice, competitorDeliveredPrice: "--" };
  }
  const competitorNetback = getNetback(
    cheapestCompetitorQuote as IQuoteExtension,
    kochPricePreference,
  );
  const competitorFreight = getFreight(cheapestCompetitorQuote?.freight);

  const competitorDeliveredPrice =
    competitorNetback.numeric !== null && competitorFreight.numeric !== null
      ? String(
          Math.round(competitorNetback.numeric + competitorFreight.numeric),
        )
      : "--";
  return { deliveredPrice, competitorDeliveredPrice };
};

export type OriginRowPricing = {
  initialDeliveredOffer: number;
  freight: number;
  initialNetback: number;
  initialNetbackDisplay: string;
  netbackSubText: string;
};

export const getOriginRowPricing = (
  quote: IQuoteExtension,
  kochDlvdOfferMode: PricePreference,
): OriginRowPricing => {
  const netback = getNetback(quote, kochDlvdOfferMode);
  const fobSubText = getNetbackSubText(quote);
  const freight = getFreight(quote.freight);

  return {
    initialDeliveredOffer:
      netback.numeric !== null && freight.numeric !== null
        ? Number((netback.numeric + freight.numeric).toFixed(0))
        : 0,
    freight: freight.numeric || 0,
    initialNetback: netback.numeric || 0,
    initialNetbackDisplay: netback.display,
    netbackSubText: fobSubText,
  };
};

// TODO: should this be a koch price?
// TODO: should we redo how we think about selected and cheapest?
// TODO: neither is price setter when there is no quote
export type DestinationQuoteRow = {
  deliveredPrice: string;
  freight: string;
  netback: string;
  netbackSubText: string;
  priceSetter: "KOCH" | "COMPETITOR" | "NEITHER";
  competitorDeliveredPrice: string;
};

const resolveDisplayValues = (
  selectedNetbackQuote: IQuoteExtension,
  kochPricePreference: PricePreference,
  cheapestKochQuote?: IQuoteExtension,
  cheapestCompetitorQuote?: IQuoteExtension,
): DestinationQuoteRow => {
  const netback = getNetback(selectedNetbackQuote, kochPricePreference);
  const netbackSubText = getNetbackSubText(selectedNetbackQuote);

  const { freight } = selectedNetbackQuote;
  const freightValues = getFreight(freight);

  const { deliveredPrice, competitorDeliveredPrice } = getDeliveredOffers(
    netback,
    freightValues,
    kochPricePreference,
    cheapestKochQuote,
    cheapestCompetitorQuote,
  );

  return {
    deliveredPrice,
    freight: freightValues.display,
    netback: netback.display,
    netbackSubText,
    priceSetter: determinePriceSetter(
      kochPricePreference,
      cheapestKochQuote,
      cheapestCompetitorQuote,
    ),
    competitorDeliveredPrice,
  };
};

export const resolveDisplayForQuote = (
  destination: IDestinationExtension,
  kochPricePreference: PricePreference,
  selectedNetbackQuote?: IQuoteExtension,
  cheapestKochQuote?: IQuoteExtension,
  cheapestCompetitorQuote?: IQuoteExtension,
): DestinationQuoteRow => {
  const hasTruckInbound =
    destination.isCity ||
    (destination.details && destination.details.inboundModes.includes("TRUCK"));
  const hasRailInbound =
    destination.details && destination.details.inboundModes.includes("RAIL");

  // if there is no cheapest quote or the destination does not have truck or rail inbound
  if (!selectedNetbackQuote || (!hasTruckInbound && !hasRailInbound)) {
    return {
      deliveredPrice: "--",
      freight: "--",
      netback: "--",
      netbackSubText: "--",
      priceSetter: "NEITHER",
      competitorDeliveredPrice: "--",
    };
  }
  return resolveDisplayValues(
    selectedNetbackQuote,
    kochPricePreference,
    cheapestKochQuote,
    cheapestCompetitorQuote,
  );
};
